import React, { useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Select, Switch, message } from 'antd'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import { LectureViewTable } from '../Lecture/styles'
import { useStudyReportTemplates } from '../../hooks/StudyReportTemplates'

type Scope = 'daily' | 'monthly' | 'both'

export default function StudyReportTemplateManage() {
	const { data: token } = useToken()
	const [form] = Form.useForm()

	const [scope, setScope] = useState<Scope>('daily')
	const [keyword, setKeyword] = useState('')
	const [includeInactive, setIncludeInactive] = useState(false)

	const { templates, mutate } = useStudyReportTemplates(scope, { includeInactive, keyword })

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editing, setEditing] = useState<any | null>(null)

	const list = useMemo(() => {
		const arr = Array.isArray(templates) ? templates : []
		return arr.sort((a: any, b: any) => {
			// Active first
			if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
			// sortOrder asc, nulls last
			const ao = a.sortOrder == null ? Number.MAX_SAFE_INTEGER : Number(a.sortOrder)
			const bo = b.sortOrder == null ? Number.MAX_SAFE_INTEGER : Number(b.sortOrder)
			if (ao !== bo) return ao - bo
			return Number(b.id) - Number(a.id)
		})
	}, [templates])

	const openCreate = () => {
		setEditing(null)
		form.resetFields()
		form.setFieldsValue({
			scope: 'daily',
			isActive: true,
			sortOrder: null,
		})
		setIsModalOpen(true)
	}

	const openEdit = (t: any) => {
		setEditing(t)
		form.resetFields()
		form.setFieldsValue({
			title: t.title || '',
			content: t.content || '',
			homework: t.homework || '',
			memo: t.memo || '',
			scope: t.scope || 'daily',
			sortOrder: t.sortOrder ?? null,
			isActive: t.isActive !== false,
		})
		setIsModalOpen(true)
	}

	const submit = async () => {
		try {
			const values = await form.validateFields()
			const payload: any = {
				title: values.title,
				content: values.content,
				homework: values.homework || null,
				memo: values.memo || null,
				scope: values.scope || 'daily',
				sortOrder: values.sortOrder === '' || values.sortOrder == null ? null : Number(values.sortOrder),
				isActive: values.isActive !== false,
			}

			if (editing?.id) {
				const res = await API.put(`/dashboard/report-templates/${editing.id}`, token + '', payload)
				if (res?.code === 0) {
					message.success('템플릿이 수정되었습니다')
					setIsModalOpen(false)
					mutate()
				} else {
					message.error('템플릿 수정에 실패했습니다')
				}
			} else {
				const res = await API.post('/dashboard/report-templates', token + '', payload)
				if (res?.code === 0) {
					message.success('템플릿이 추가되었습니다')
					setIsModalOpen(false)
					mutate()
				} else {
					message.error('템플릿 추가에 실패했습니다')
				}
			}
		} catch (err: any) {
			if (err?.errorFields) return
			message.error(err?.message || '저장 중 오류가 발생했습니다')
		}
	}

	const softDelete = async (t: any) => {
		if (!window.confirm(`'${t.title}' 템플릿을 삭제(비활성)하시겠습니까?`)) return
		try {
			const res = await API.delete(`/dashboard/report-templates/${t.id}`, token + '', {})
			if (res?.code === 0) {
				message.success('삭제(비활성)되었습니다')
				mutate()
			} else {
				message.error('삭제에 실패했습니다')
			}
		} catch (err: any) {
			message.error(err?.message || '삭제 중 오류가 발생했습니다')
		}
	}

	const toggleActive = async (t: any) => {
		try {
			const res = await API.put(`/dashboard/report-templates/${t.id}`, token + '', { isActive: !t.isActive })
			if (res?.code === 0) {
				message.success(!t.isActive ? '활성화되었습니다' : '비활성화되었습니다')
				mutate()
			} else {
				message.error('상태 변경에 실패했습니다')
			}
		} catch (err: any) {
			message.error(err?.message || '상태 변경 중 오류가 발생했습니다')
		}
	}

	return (
		<div>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
				<div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
					<b style={{ fontSize: '16px' }}>학습일지 템플릿 관리</b>
					<Select
						value={scope}
						style={{ width: 140 }}
						onChange={(v: Scope) => setScope(v)}
						options={[
							{ label: '일일(daily)', value: 'daily' },
							{ label: '월간(monthly)', value: 'monthly' },
							{ label: '공용(both)', value: 'both' },
						]}
					/>
					<Input
						placeholder="제목 검색"
						style={{ width: 220 }}
						value={keyword}
						onChange={e => setKeyword(e.target.value)}
					/>
					<div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
						<span style={{ fontSize: '13px', color: '#666' }}>비활성 포함</span>
						<Switch checked={includeInactive} onChange={setIncludeInactive} />
					</div>
				</div>
				<Button type="primary" onClick={openCreate}>
					템플릿 추가
				</Button>
			</div>

			<LectureViewTable>
				<thead>
					<tr>
						<th style={{ width: 90 }}>상태</th>
						<th>제목</th>
						<th style={{ width: 120 }}>범위</th>
						<th style={{ width: 120 }}>정렬</th>
						<th style={{ width: 160 }}>관리</th>
					</tr>
				</thead>
				<tbody>
					{list.length === 0 ? (
						<tr>
							<td colSpan={5} style={{ color: '#666' }}>
								템플릿이 없습니다.
							</td>
						</tr>
					) : (
						list.map((t: any) => (
							<tr key={t.id} style={{ opacity: t.isActive ? 1 : 0.5 }}>
								<td>{t.isActive ? '활성' : '비활성'}</td>
								<td style={{ textAlign: 'left' }}>
									<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
										<b>{t.title}</b>
										<span style={{ fontSize: '12px', color: '#666' }}>
											{(t.content || '').slice(0, 80)}
											{(t.content || '').length > 80 ? '…' : ''}
										</span>
									</div>
								</td>
								<td>{t.scope}</td>
								<td>{t.sortOrder == null ? '-' : t.sortOrder}</td>
								<td>
									<div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
										<button onClick={() => openEdit(t)} style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #d9d9d9', background: '#fff' }}>
											수정
										</button>
										<button
											onClick={() => toggleActive(t)}
											style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #d9d9d9', background: '#fff' }}>
											{t.isActive ? '비활성' : '활성'}
										</button>
										<button
											onClick={() => softDelete(t)}
											style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #e54848', background: '#e54848', color: '#fff' }}>
											삭제
										</button>
									</div>
								</td>
							</tr>
						))
					)}
				</tbody>
			</LectureViewTable>

			<Modal
				title={editing ? '템플릿 수정' : '템플릿 추가'}
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				onOk={submit}
				okText="저장"
				cancelText="취소"
				width={720}>
				<Form form={form} layout="vertical">
					<Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력해주세요' }]}>
						<Input placeholder="예) 기본 학습일지" />
					</Form.Item>
					<Form.Item name="content" label="학습내용" rules={[{ required: true, message: '학습내용을 입력해주세요' }]}>
						<Input.TextArea rows={5} placeholder="학습내용 템플릿" />
					</Form.Item>
					<Form.Item name="homework" label="과제">
						<Input.TextArea rows={3} placeholder="과제 템플릿 (선택)" />
					</Form.Item>
					<Form.Item name="memo" label="비고">
						<Input placeholder="비고 템플릿 (선택)" />
					</Form.Item>
					<div style={{ display: 'flex', gap: '12px' }}>
						<Form.Item name="scope" label="범위" initialValue="daily" style={{ flex: 1 }}>
							<Select
								options={[
									{ label: 'daily', value: 'daily' },
									{ label: 'monthly', value: 'monthly' },
									{ label: 'both', value: 'both' },
								]}
							/>
						</Form.Item>
						<Form.Item name="sortOrder" label="정렬순서" style={{ width: 160 }}>
							<Input placeholder="숫자 (선택)" />
						</Form.Item>
						<Form.Item
							name="isActive"
							label="활성"
							valuePropName="checked"
							initialValue={true}
							style={{ width: 120 }}>
							<Switch />
						</Form.Item>
					</div>
				</Form>
			</Modal>
		</div>
	)
}









